import clsx from 'clsx';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { positionState, selectQuantity } from '../../helpers/taxPlanner/fifoSelection';
import { D } from '../../helpers/taxPlanner/money';
import { positionKey } from '../../helpers/taxPlanner/totals';
import {
  changeStrategic,
  clearNotice,
  showToast,
  recalculate,
  repick,
  selectImpact,
  selectLotPersisted,
  setQuantityPersisted,
  togglePositionPersisted,
  unselectLotPersisted,
} from '../../store/slices/taxPlannerSlice';
import Banner from '../Banner';
import Card from '../Card';
import TableToolbar from './TableToolbar';
import LotRow from './LotRow';
import { pluralForm } from '../../helpers/pluralForm';
import PositionRow, { currencySymbol, formatRate, formatUah, isoToDotted } from './PositionRow';
import { jurisdiction } from '../../config/taxPlannerJurisdiction';

// «Переглянути позиції» in the summary scrolls here (B-28).
export const POSITIONS_ID = 'tax-planner-positions';

const PARTICIPATING = 'PARTICIPATING';
// Mock `table.pos th`.
const TH = 'bg-surface-alt px-2 py-2.5 align-bottom text-[11px] font-semibold tracking-[.02em] text-text-muted';
const COLUMNS = ['position', 'quantity', 'price', 'unrealized', 'unrealizedUah', 'sale', 'saleResult', 'impact', 'strategic'];

// Accounts with a current-year calculation, grouped by report date (AC-14).
const sourceGroups = (accounts) => {
  const groups = new Map();
  accounts
    .filter((a) => a.participation === PARTICIPATING && a.dataAsOf)
    .forEach((a) => {
      if (!groups.has(a.dataAsOf)) groups.set(a.dataAsOf, []);
      groups.get(a.dataAsOf).push(a);
    });
  return [...groups.entries()];
};

const rateLabels = (accounts) => {
  const seen = new Set();
  const labels = [];
  accounts.forEach((a) =>
    (a.exchangeRates || []).forEach(({ currency, rate }) => {
      const label = `${formatRate(rate)} ${jurisdiction.currencySymbol}/${currencySymbol(currency)}`;
      if (!seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    }),
  );
  return labels;
};

// The last system pick (state.taxPlanner.lastPick): info by default, warn when losses were not enough, ok when none was needed.
function PickBanner({ pick, rounding }) {
  const { t, i18n } = useTranslation();
  const k = (key, opts) => t(`taxPlanner.scr02.table.pick.${key}`, opts);
  if (!pick) return null;
  let tone = 'info';
  let message;
  if (pick.reason) {
    tone = 'ok';
    message = k(pick.reason);
  } else if (pick.short) {
    tone = 'warn';
    message = k('short', { count: pick.summary.count });
  } else {
    const { count, afterSale, target } = pick.summary;
    message = k(`default_${pluralForm(count, i18n.language)}`, { count, result: formatUah(afterSale, rounding), target: formatUah(target, rounding) });
  }
  return (
    <div data-testid="pick-banner">
      <Banner tone={tone}>{message}</Banner>
    </div>
  );
}

function PositionsTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sub = useAuth().user?.profile?.sub;
  const k = (key, opts) => t(`taxPlanner.scr02.positions.${key}`, opts);
  const snapshot = useSelector((state) => state.taxPlanner.snapshot);
  const selection = useSelector((state) => state.taxPlanner.selection);
  const loss = useSelector((state) => state.taxPlanner.params.loss);
  const strategic = useSelector((state) => state.taxPlanner.params.strategic);
  const lastPick = useSelector((state) => state.taxPlanner.lastPick);
  const beforeStrategic = useSelector((state) => state.taxPlanner.beforeStrategic);
  const notice = useSelector((state) => state.taxPlanner.notice);
  const [expanded, setExpanded] = useState({});
  const strategicNotSaved = useSelector((state) => state.taxPlanner.persistFailed.strategic);
  const setToast = (message) => dispatch(showToast(message));
  const tickerOf = (key) => snapshot?.positions.find((p) => positionKey(p) === key)?.ticker ?? key;
  const toastText = (name, ticker) => t(`taxPlanner.scr02.table.toast.${name}`, { ticker });

  useEffect(() => {
    if (notice?.type === 'STRATEGIC_UNMARKED') {
      setToast(t('taxPlanner.scr02.table.toast.unmarked', { ticker: tickerOf(notice.key) }));
      dispatch(clearNotice());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notice]);

  const impact = useMemo(
    () => (snapshot ? selectImpact({ taxPlanner: { snapshot, selection, params: { loss } } }) : null),
    [snapshot, selection, loss],
  );

  if (!snapshot) return null;
  const rounding = jurisdiction.rounding.mode;
  const accounts = snapshot.accounts || [];
  const accountOf = (id) => accounts.find((a) => a.investingAccountId === id);
  const impactOf = (key) => impact.positions.find((p) => p.key === key);
  // B-39: the rates of the impact note are the trading rates of this snapshot.
  const tradingRates = snapshot.realized.buckets.find((b) => b.bucket === 'TRADING')?.rates || [];
  const taxes = tradingRates.map((r) => `${k(`footnoteTax.${r.taxType}`)} ${D(r.rate).times(100).toFixed()}%`).join(' + ');
  const footnotes = k('footnotes', { returnObjects: true, taxes });
  const empty = snapshot.positions.length === 0;

  const rows = snapshot.positions.map((position) => {
    const key = positionKey(position);
    const quantity = selection[key];
    const sel = quantity && Number(quantity) > 0 ? selectQuantity(position, quantity) : null;
    return { position, key, selected: sel?.ok ? sel : null };
  });
  const chosen = rows.filter((r) => r.selected);
  const saleResult = chosen.reduce((sum, r) => sum.plus(r.selected.resultUah), D(0));

  const onRecalculate = () => {
    dispatch(recalculate());
    setToast(toastText('recalculated'));
  };
  const onRepick = () => {
    dispatch(repick());
    setToast(toastText('repicked'));
  };
  const onStrategic = (key) => {
    const ticker = tickerOf(key);
    if (strategic.includes(key)) setToast(toastText(beforeStrategic[key] !== undefined ? 'strategicOffRestored' : 'strategicOff', ticker));
    else setToast(toastText(selection[key] !== undefined ? 'strategicOnSelected' : 'strategicOn', ticker));
    dispatch(changeStrategic({ sub, key }));
  };

  return (
    <section id={POSITIONS_ID} data-testid="positions-section" className="flex flex-col gap-3">
      <div>
        <h2 className="text-lg font-semibold text-text">{k('title')}</h2>
        <p data-testid="positions-subtitle" className="text-sm text-text-muted">{k('subtitle')}</p>
      </div>
      <div data-testid="price-source" className="flex flex-col gap-1 text-sm text-text-muted">
        <span>{k('priceSource')}</span>
        {sourceGroups(accounts).map(([date, group]) => {
          const byBroker = new Map();
          group.forEach((a) => {
            if (!byBroker.has(a.dataProvider)) byBroker.set(a.dataProvider, []);
            byBroker.get(a.dataProvider).push(a.originalId);
          });
          const names = [...byBroker.entries()]
            .map(([provider, ids]) => `${t(`taxPlanner.scr02.positions.brokerShort.${provider}`, { defaultValue: provider })} ${ids.join(', ')}`)
            .join('; ');
          const rates = rateLabels(group);
          return (
            <span key={date} data-testid="price-source-group" className="num">
              {[k('sourceGroup', { accounts: names, date: isoToDotted(date) }), rates.length ? k('sourceRates', { rates: rates.join(' · ') }) : null]
                .filter(Boolean)
                .join(' · ')}
            </span>
          );
        })}
      </div>
      {/* One banner at a time (B-38): an empty table says it has nothing to sell, otherwise the last pick. */}
      {strategicNotSaved && (
        <div data-testid="strategic-not-saved">
          <Banner tone="warn">{t('taxPlanner.scr02.table.strategicNotSaved', { ticker: tickerOf(strategicNotSaved) })}</Banner>
        </div>
      )}
      {empty ? <Banner tone="ok">{k('emptyBanner')}</Banner> : <PickBanner pick={lastPick} rounding={rounding} />}
      <Card data-testid="positions-card" className="overflow-hidden">
        <TableToolbar count={chosen.length} result={formatUah(saleResult, rounding)} onRepick={onRepick} onRecalculate={onRecalculate} />
        {/* contain: the 960 px table scrolls inside the card and never widens the page (360 px, spec §6) */}
        <div data-testid="positions-scroll" className="relative overflow-x-auto [contain:inline-size]">
        <table data-testid="positions-table" className="w-full min-w-[960px] border-collapse text-[13px]">
          <thead>
            <tr>
              <th scope="col" className={clsx(TH, 'w-16')} />
              {COLUMNS.map((col, i) => (
                <th key={col} scope="col" className={clsx(TH, i === 0 ? 'text-left' : col === 'strategic' ? 'w-[88px] text-center' : 'text-right')}>
                  {k(`columns.${col}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {empty && (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="px-4 py-6 text-center text-text-muted">{k('empty')}</td>
              </tr>
            )}
            {rows.map(({ position, key, selected }) => {
              const posImpact = impactOf(key);
              const state = selected ? positionState(position, selected.quantity) : 'none';
              const open = expanded[key] ?? state === 'partial';
              return (
                <Fragment key={key}>
                  <PositionRow
                    position={position}
                    account={accountOf(position.investingAccountId)}
                    strategic={strategic.includes(key)}
                    state={state}
                    selection={selected}
                    impact={posImpact}
                    expanded={open}
                    rounding={rounding}
                    onToggleExpand={() => setExpanded((e) => ({ ...e, [key]: !open }))}
                    onToggleSelect={() => dispatch(togglePositionPersisted({ sub, key }))}
                    onToggleStrategic={() => onStrategic(key)}
                    onChangeQuantity={(quantity) => dispatch(setQuantityPersisted({ sub, key, quantity }))}
                  />
                  {open &&
                    position.lots.map((lot) => {
                      const index = selected ? selected.lots.findIndex((l) => l.fifoOrder === lot.fifoOrder) : -1;
                      const toggleLot = index >= 0 ? unselectLotPersisted : selectLotPersisted;
                      return (
                        <LotRow
                          key={lot.fifoOrder}
                          position={position}
                          lot={lot}
                          sold={index >= 0 ? selected.lots[index] : undefined}
                          resultUah={index >= 0 ? selected.lotResultsUah[index] : undefined}
                          impact={posImpact?.lots.find((l) => l.fifoOrder === lot.fifoOrder)}
                          rounding={rounding}
                          onToggleSelect={() => dispatch(toggleLot({ sub, key, n: lot.fifoOrder }))}
                        />
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-border px-4 py-3 text-xs text-text-muted">
          {Array.isArray(footnotes) && footnotes.map((note) => <span key={note} data-testid="positions-footnote">{note}</span>)}
        </div>
      </Card>
    </section>
  );
}

export default PositionsTable;
